import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  CircularProgress,
  colors,
  Container,
  Grid,
  InputAdornment,
  makeStyles,
  MenuItem,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  AiFillEdit,
  AiOutlineCheckCircle,
  AiOutlineSearch,
} from "react-icons/ai";

import useTabPanelStyles from "../../styles/TabPanel.style";
import useStyles from "./CountriesTabPanel.style";
import TableData, {
  StyledTableCell,
  StyledTableRow,
} from "../../components/TableData";
import Country from "../../../../models/Country";
import Paginator from "../../../../models/Paginator";
import moment from "moment";
import { ActionButton } from "../../components/Buttons";
import { InputField } from "../../../../components/input-field/InputField";
import { Status, StatusType } from "../../components/Status";
import { EnableStatus } from "../../../../settings/EnableStatus";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";
import { PageTitle } from "../../../../components/page-title/PageTitle";
import { InputButton } from "../../../../components/input-button/InputButton";
import Modal from "../../components/Modal";
import { AddRequestCountryAttributes } from "../../../../attributes/country-attributes";
import { InputSelect } from "../../../../components/input-select/InputSelect";
import CountryService from "../../../../services/CountryService";

interface CountriesTabPanelProps {
  paginator: Paginator;
  countries: any;
}

interface CountryActivationAttr {
  id: number;
}

export const CountriesTabPanel = (props: CountriesTabPanelProps) => {
  const classes = useStyles(props);
  const tabPanelClasses = useTabPanelStyles(props);
  const intl = useIntl();
  // Props
  const { paginator } = props;
  // State
  const [countries, setCountries] = useState(paginator.items);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [activating, setActivating] = useState(
    (): Array<CountryActivationAttr> => []
  );

  // Callback

  const activatingItemIndex = (key: number): number => {
    return activating.findIndex((item) => item.id === key);
  };

  const updateActivatingItems = (key: number, remove: boolean = false) => {
    let index = activatingItemIndex(key);
    let data = activating;

    if (remove) {
      if (index >= 0) {
        data.splice(index, 1);

        setActivating([...data]);
      }
    } else {
      if (index < 0) {
        data.push({ id: key });

        setActivating([...data]);
      }
    }
  };

  const countryActivation = (countryId: number) => {
    updateActivatingItems(countryId);
    const response = CountryService.countryActivation(countryId);

    response.then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let country = Country.fromJson(
          JSON.parse(JSON.stringify(response.data.data))
        );
        handleUpdateCountry(country);
      } else {
      }

      updateActivatingItems(countryId, true);
    });
  };

  const handleModalHandler = (state: boolean) => {
    setSelectedCountry(null);
    setOpenModal(state);
  };

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
    setOpenModal(true);
  };

  const handleUpdateCountry = (country: Country) => {
    let oldCountries = countries;

    let index = countries
      .map(function (item) {
        return item.id;
      })
      .indexOf(country.id);

    oldCountries[index] = country;

    // Update
    setCountries([...oldCountries]);
    setOpenModal(false);
    setSelectedCountry(null);
  };

  const searchFilter = (query: string) => {
    if (query !== "") {
      const results = countries.filter((country: Country) => {
        let searchCountryName = country.designation
          .toLowerCase()
          .startsWith(query.toLowerCase());
        let searchCountryCode = country.iso_code
          .toLowerCase()
          .startsWith(query.toLowerCase());
        let searchCurrencyName = country.currency.designation
          .toLowerCase()
          .startsWith(query.toLowerCase());

        return searchCountryName || searchCountryCode || searchCurrencyName;
      });
      setCountries(results);
    } else {
      setCountries(paginator.items);
    }
  };

  // Renders

  const render = () => {
    return (
      <Box component="div">
        <TableData
          paginator={
            paginator.total !== 0
              ? {
                  total: paginator.total,
                  page: paginator.page,
                  totalPage: paginator.total_page,
                  title: renderCountriesSearch(),
                  handlePrevAction:
                    paginator.prev_page !== null ? () => {} : null,
                  handleNextAction:
                    paginator.next_page !== null ? () => {} : null,
                }
              : null
          }
          rows={renderCountriesTableRows()}
          header={renderCountriesTableHeader()}
        />
      </Box>
    );
  };

  const renderCountriesTableRows = () => {
    return countries.map((row, index) => {
      let country = row as Country;

      return (
        <StyledTableRow key={index}>
          <StyledTableCell component="td" scope="row">
            {`${country.designation} (${country.iso_code})`}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {country.dial_code}
          </StyledTableCell>
          <StyledTableCell
            component="td"
            scope="row"
            className={classes.blueCell}
          >
            {country.currency.code}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            <Status
              withBorder
              withUppercase
              size={11}
              type={StatusType.enableStatus}
              status={country.status()}
            />
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {moment(country.created_at).format("ddd, MMM yyyy @hh:mm")}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {country.status() !== EnableStatus.activated ? (
              <>
                {activatingItemIndex(country.id) < 0 ? (
                  <ActionButton
                    ariaLabel="Update country"
                    customColor="#26a69a"
                    title={intl.formatMessage({ id: "sharedScreen.activate" })}
                    onClick={(_) => countryActivation(country.id)}
                  >
                    <AiOutlineCheckCircle />
                  </ActionButton>
                ) : (
                  <Box component="span" mr={2} display="inline-block">
                    <CircularProgress
                      color="secondary"
                      disableShrink={false}
                      size={14}
                      thickness={4}
                    />
                  </Box>
                )}

                <ActionButton
                  ariaLabel="Update country"
                  title={intl.formatMessage({ id: "sharedScreen.update" })}
                  onClick={(_) => handleSelectCountry(country)}
                >
                  <AiFillEdit />
                </ActionButton>
              </>
            ) : (
              <></>
            )}
          </StyledTableCell>
        </StyledTableRow>
      );
    });
  };

  const renderCountriesTableHeader = () => {
    return (
      <TableRow>
        <StyledTableCell>
          <FormattedMessage id="sharedScreen.name" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.callingCode" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.currency" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.status" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.addedAt" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.actions" />
        </StyledTableCell>
      </TableRow>
    );
  };

  const renderCountriesSearch = () => {
    return (
      <Box width="60%" marginBottom={3}>
        <InputField
          id="search"
          name="search"
          fullWidth
          placeholder={intl.formatMessage({
            id: "sharedScreen.typeToSearch",
          })}
          type="text"
          endAdornment={
            <InputAdornment position="end">
              <AiOutlineSearch />
            </InputAdornment>
          }
          onChange={(event) => searchFilter(event.target.value)}
        />
        <Box component="div" marginTop={1} className={classes.caption}>
          Ex: Cameroon, dollar, usd, +233 ...
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (paginator.items.length !== countries.length) {
      setCountries([...paginator.items]);
    }

    // eslint-disable-next-line
  }, [paginator.items]);

  return (
    <>
      <Box component="div" className={tabPanelClasses.root}>
        <Box component="div" className={classes.tabsPanel}>
          <Box component="div" className={classes.wrapper}>
            <Box component="div" className={classes.leftHeader}>
              <Typography variant="h3" component="h3">
                <FormattedMessage id="sharedScreen.supportedCountries" />
              </Typography>

              <Box component="div" className={classes.trailing}>
                <Typography variant="h3" component="h3">
                  {`# ${paginator.total}`}
                </Typography>
              </Box>
            </Box>
            <Box component="div" className={classes.leftWrapper}>
              {render()}
            </Box>
          </Box>
        </Box>

        <Modal open={openModal} onClose={() => handleModalHandler(false)}>
          <UpdateCountryForm
            country={selectedCountry}
            callback={handleUpdateCountry}
          />
        </Modal>
      </Box>
    </>
  );
};

interface UpdateCountryFormProps {
  country: Country;
  callback: (country: Country) => void;
}

const useUpdateCountryStyles = makeStyles(
  (theme) => ({
    root: {},

    wrapper: {
      "& h4": {
        fontWeight: theme.typography.fontWeightBold,
      },
    },

    header: {
      textAlign: "left",
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(2),
    },

    subTitle: {
      display: "block",
      fontSize: 13,
      color: colors.grey[600],
      marginTop: theme.spacing(1),
    },

    formWrapper: {
      marginTop: theme.spacing(2),
    },

    grid: {
      margin: 0,
      padding: 0,
      flexGrow: 1,
    },

    loader: {
      marginTop: 6,
    },

    error: {
      minWidth: 50,
      fontSize: 13,
      color: colors.red[800],
    },

    gridItem: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(2),
    },

    link: {
      color: theme.palette.info.main,
    },

    menuPaper: {
      maxHeight: 300,
    },

    currencyItem: {
      fontSize: 14,
    },

    currencySearchWrapper: {},
  }),
  { name: "update-country-form-styles" }
);

function UpdateCountryForm(props: UpdateCountryFormProps) {
  const classes = useUpdateCountryStyles(props);
  const intl = useIntl();
  // Props
  const { country, callback } = props;
  // State
  const [currencies, setCurrencies] = useState(() => null);
  const [defaultCurrencyIndex, setDefaultCurrencyIndex] = useState(() => 0);

  // Events

  const handleCurrencyChange = (event: any, child: any) => {
    formik.values.currency = event.target.value;
    setDefaultCurrencyIndex(event.target.value);
  };

  const defaultCurrency = (data: Array<any>): number => {
    return data.findIndex((item) => item.code === country.currency.code);
  };

  // Requests

  const loadCurrencies = () => {
    const response = CountryService.supportedCurrencies();

    response.then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let currencies = JSON.parse(JSON.stringify(response.data.data));

        setCurrencies(currencies);
        setDefaultCurrencyIndex(defaultCurrency(currencies));
      } else {
        setCurrencies([]);
      }
    });
  };

  // Handle formik

  const initFormValues = () => {
    return {};
  };

  const formValidation = () => {
    return {};
  };

  const formik = useFormik({
    initialValues: initFormValues(),
    validationSchema: Yup.object().shape({
      ...formValidation(),
    }),
    onSubmit: async (
      values: AddRequestCountryAttributes,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        let currency = currencies[formik.values.currency];
        const response = await CountryService.updateCountry(
          country.id,
          currency.code,
          currency.designation
        );
        setSubmitting(false);
        if (response.isSuccess) {
          switch (response.data.code) {
            case 100: // Ok
              let country = Country.fromJson(
                JSON.parse(JSON.stringify(response.data.data))
              );
              callback(country);
              break;
            case 150:
              // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            default:
              setErrors({ error: response.data.message });
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (error) {
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  // Render

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors.error) {
      return formik.errors.error as string;
    }

    return "";
  };

  const renderCurrencies = () => {
    if (currencies === null) {
      return (
        <MenuItem value="" disabled>
          <FormattedMessage id="sharedScreen.loading" />
        </MenuItem>
      );
    }

    if (currencies.length === 0) {
      return (
        <MenuItem value="" disabled>
          <Box component="div">
            <FormattedMessage id="sharedScreen.noCurrenciesFound" />
            <Box component="span" className={classes.link}>
              <FormattedMessage id="sharedScreen.reload" />
            </Box>
          </Box>
        </MenuItem>
      );
    }

    return currencies.map((currency, index) => {
      return (
        <MenuItem value={index} key={index}>
          <span className={classes.currencyItem}>
            {currency.code}
            {currency.designation !== null ? ` (${currency.designation})` : ""}
          </span>
        </MenuItem>
      );
    });
  };

  // renders

  useEffect(() => {
    loadCurrencies();

    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${country.designation}`}
      />
      <Box className={classes.root}>
        <Container maxWidth="sm" className={classes.wrapper}>
          <Box component="div" className={classes.header} mb={4}>
            <Typography variant="h4" component="h4">
              <FormattedMessage
                id="sharedScreen.updateCountry"
                values={{ country: country.designation }}
              />
            </Typography>
            <Box component="span" className={classes.subTitle}>
              <FormattedMessage id="sharedScreen.informationOfUpdateCountryNotice" />
            </Box>
          </Box>

          <Box component="div" className={classes.formWrapper}>
            <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
              <Box mb={3} ml={1} mr={2}>
                <InputField
                  id="country_designation"
                  name="country_designation"
                  fullWidth
                  label={intl.formatMessage({
                    id: "sharedScreen.countryName",
                  })}
                  placeholder=""
                  type="text"
                  disabled={true}
                  value={country.designation}
                />
              </Box>
              <Box component="div" mb={3}>
                <Grid
                  container
                  wrap="wrap"
                  className={classes.grid}
                  justify="space-between"
                >
                  <Grid item xs={12} sm={6} className={classes.gridItem}>
                    <InputField
                      id="alpha_code"
                      name="alpha_code"
                      fullWidth
                      label={intl.formatMessage({
                        id: "sharedScreen.alphaCode",
                      })}
                      type="text"
                      disabled={true}
                      value={country.iso_code}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.gridItem}>
                    <InputField
                      id="country_dc"
                      name="country_dc"
                      fullWidth
                      label={intl.formatMessage({
                        id: "sharedScreen.callingCode",
                      })}
                      type="text"
                      disabled={true}
                      value={country.dial_code}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box mb={6} ml={1} mr={2}>
                <InputSelect
                  fullWidth
                  id="currency"
                  name="currency"
                  value={defaultCurrencyIndex}
                  defaultValue={defaultCurrencyIndex}
                  label={intl.formatMessage({
                    id: "sharedScreen.currency",
                  })}
                  MenuProps={{
                    classes: { paper: classes.menuPaper },
                  }}
                  onChange={handleCurrencyChange}
                  error={
                    Boolean(formik.touched.currency) &&
                    Boolean(formik.errors.currency)
                  }
                  helperText={formik.touched.currency && formik.errors.currency}
                >
                  <MenuItem value="" disabled>
                    <FormattedMessage id="sharedScreen.selectBusinessType" />
                  </MenuItem>
                  {renderCurrencies()}
                </InputSelect>
              </Box>

              <Box
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mr={2}
              >
                <Box component="div" className={classes.error}>
                  {genericError()}
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  {formik.isSubmitting ? (
                    <Box
                      display="inline-block"
                      mr={4}
                      className={classes.loader}
                    >
                      <CircularProgress
                        color="secondary"
                        size={20}
                        thickness={4}
                      />
                    </Box>
                  ) : (
                    <></>
                  )}
                  <Box display="inline-block">
                    <InputButton
                      color="secondary"
                      fullWidth
                      size="medium"
                      variant="contained"
                      type="submit"
                      disabled={formik.isSubmitting}
                    >
                      <FormattedMessage id="sharedScreen.update" />
                    </InputButton>
                  </Box>
                </Box>
              </Box>
            </form>
          </Box>
        </Container>
      </Box>
    </>
  );
}
